import { keyframes, styled } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

export const slideInFromTop = keyframes`
  0% { opacity: 0; transform: translateY(-10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

export const Wrapper = styled('div')({
  borderTop: '1px solid #eef0f4',
});

export const ModeSelectorWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.6rem 1.2rem',
  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  borderBottom: '1px solid #e2e8f0',
  animation: `${slideInFromTop} 0.3s ease-out`,
});

export const ModeLabel = styled('label')({
  fontSize: 14,
  fontWeight: 700,
  color: '#475569',
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

export const ModeSelect = styled('button')({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '2px solid #cbd5e1',
  borderRadius: 10,
  color: '#1e293b',
  padding: '0.5rem 0.85rem',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.2,
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  minWidth: 180,
  '&:hover': {
    borderColor: '#0a80e0',
    background: 'linear-gradient(135deg, #ffffff 0%, #e0f2fe 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(10, 128, 224, 0.15), 0 2px 4px rgba(10, 128, 224, 0.1)',
  },
  '&:focus': {
    outline: 'none',
    borderColor: '#0a80e0',
    background: 'linear-gradient(135deg, #ffffff 0%, #dbeafe 100%)',
    boxShadow: '0 0 0 4px rgba(10, 128, 224, 0.12), 0 4px 12px rgba(10, 128, 224, 0.2)',
    transform: 'scale(1.02)',
  },
  '&:active': {
    transform: 'scale(0.98)',
    boxShadow: '0 1px 3px rgba(10, 128, 224, 0.2)',
  },
  '&:disabled': {
    cursor: 'default',
    opacity: 0.6,
    borderColor: '#cbd5e1',
    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
    color: '#94a3b8',
    boxShadow: 'none',
    transform: 'none',
  },
});

export const DropdownArrow = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ isOpen }) => ({
  fontSize: 10,
  marginLeft: '0.5rem',
  transition: 'transform 0.3s ease',
  transform: isOpen ? 'rotate(180deg)' : 'none',
  color: '#64748b',
}));

export const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': {
    marginTop: '8px',
    width: 300,
    borderRadius: '12px',
    border: '2px solid #cbd5e1',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)',
  },
  '& .MuiList-root': {
    padding: 0,
  },
});

export const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive }) => ({
  width: '100%',
  padding: '0.75rem 1rem',
  background: isActive ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' : '#ffffff',
  borderBottom: '1px solid #f1f5f9',
  borderLeft: isActive ? '3px solid #0a80e0' : '3px solid transparent',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.75rem',
  whiteSpace: 'normal',
  '&:last-child': { borderBottom: 'none' },
  '&:hover': {
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
  },
}));

export const MenuOptionContent = styled('div')({
  flex: 1,
});

export const MenuOptionTitle = styled('div')({
  fontSize: 14,
  fontWeight: 700,
  color: '#1e293b',
  marginBottom: '0.25rem',
});

export const MenuOptionDesc = styled('div')({
  fontSize: 13,
  color: '#475569',
  lineHeight: 1.2,
  fontWeight: 500,
});

export const InputArea = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 0.9rem 0.75rem',
});

export const Input = styled('input')({
  flex: 1,
  padding: '0.75rem 1rem',
  borderRadius: 20,
  border: '2px solid #0a80e0',
  fontSize: 16,
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&:focus': {
    outline: 'none',
    borderColor: '#0a80e0',
    boxShadow: '0 0 0 3px rgba(10, 128, 224, 0.1)',
  },
});

export const SendButton = styled('button')({
  backgroundColor: '#0a80e0',
  color: '#fff',
  border: 'none',
  borderRadius: '50%',
  width: 44,
  height: 44,
  fontSize: 20,
  cursor: 'pointer',
  transition: 'transform 0.05s ease, opacity 0.15s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:disabled': { opacity: 0.6, cursor: 'default' },
  '&:active': { transform: 'translateY(1px)' },
});

export const Disclaimer = styled('div')({
  padding: '0.5rem 0.9rem 0.5rem',
  fontSize: '20px',
  color: '#9ca3af',
  textAlign: 'center',
  lineHeight: 1.3,
  background: '#fafbfc',
});
